import { Injectable } from '@angular/core';
import {
	FormationThematique,
	FormationThematiqueInterface,
	FormationThematiquePayload,
} from './formation-thematique';
import { FormationThematiqueSearchParamsInterface } from './formation-thematique-search-params';
import { BaseModelService } from '@app/abstracts';
import { Helpers } from '@app/shared/helpers';
import {
	AttestationPresenceThematiqueSummary,
	AttestationPresenceThematiqueSummaryInterface,
} from '@app/models/attestation-presence-thematique-summary/attestation-presence-thematique-summary';

export interface FormationThematiqueMetadata {
	attestation_presence_dates: AttestationPresenceDate[];
}

export interface AttestationPresenceDate {
	signatureDateTime: Date | string;
	period: 'afternoon' | 'morning';
	signed: boolean;
}

export interface FormationThematiqueFormateurMetadata {
	attestation_presence_dates: FormateurAttestationGroup[];
}

export interface FormateurAttestationGroup {
	signatureDateTime: string | Date;
	period: 'afternoon' | 'morning';
	stagiaires: BackendStagiaireData[];
}

export interface BackendStagiaireData {
	stagiaire: {
		_id: string;
		first_name: string;
		last_name: string;
		email?: string;
	};
	attestation?: {
		_id: string;
		created_at: number;
		date: number;
		document?: string;
		delay?: number;
		excused?: boolean;
		signature?: string;
	} | null;
}

export interface FormateurStagiaireSignature {
	stagiaire_id: string;
	stagiaire_name: string;
	signed: boolean;
	excused: boolean;
	signed_at?: number;
	delay?: number;
	attestation_id?: string;
}

@Injectable()
export class FormationThematiqueService extends BaseModelService<
	FormationThematique,
	FormationThematiqueInterface,
	FormationThematiquePayload,
	FormationThematiqueSearchParamsInterface
> {
	/** @inheritDoc */
	protected path(): string {
		return 'formation-thematique';
	}
	/** @inheritDoc */
	protected new(object: FormationThematiqueInterface): FormationThematique {
		return new FormationThematique(object);
	}
	/** @inheritDoc */
	protected transformSearchParams(
		searchParams: FormationThematiqueSearchParamsInterface
	): FormationThematiqueSearchParamsInterface {
		// Search by edge for Start Date
		if (typeof searchParams.start_date__min !== 'undefined') {
			searchParams.start_date__min = Helpers.toMidnightUTC(
				searchParams.start_date__min
			);
		}
		if (typeof searchParams.start_date__max !== 'undefined') {
			searchParams.start_date__max = Helpers.toLastSecondUTC(
				searchParams.start_date__max
			);
		}
		if (typeof searchParams.start_date !== 'undefined') {
			searchParams.start_date__min = Helpers.toMidnightUTC(
				searchParams.start_date
			);
			searchParams.start_date__max = Helpers.toLastSecondUTC(
				searchParams.start_date
			);
			delete searchParams.start_date;
		}
		return searchParams;
	}

	getMetadata(
		id: string,
		asAdmin = false
	): Promise<FormationThematiqueMetadata> {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		// Start request
		const options = {
			withCredentials: !this.publicRead,
			params: { timezone },
		};
		return this.http
			.get<FormationThematiqueMetadata>(
				`${this.uri(asAdmin)}/${id}/metadata`,
				options
			)
			.toPromise();
	}

	getFormateurMetadata(
		id: string,
		asAdmin = false
	): Promise<FormationThematiqueFormateurMetadata> {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		// Start request
		const options = {
			withCredentials: !this.publicRead,
			params: { timezone },
		};
		return this.http
			.get<FormationThematiqueFormateurMetadata>(
				`${this.uri(asAdmin)}/${id}/metadata/formateur`,
				options
			)
			.toPromise();
	}

	/**
	 * Get admin metadata for a thematique (same as formateur but for admin users)
	 */
	getAdminMetadata(
		id: string
	): Promise<FormationThematiqueFormateurMetadata> {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const options = {
			withCredentials: true,
			params: { timezone },
		};
		return this.http
			.get<FormationThematiqueFormateurMetadata>(
				`${this.uri(true)}/${id}/metadata`,
				options
			)
			.toPromise();
	}

	/**
	 * Generate presence summary document for a thematique
	 * @param thematiqueId - Thematique ID
	 * @param data - Optional data for document generation
	 * @param asAdmin - Whether to use admin endpoint
	 * @returns Generated attestation presence thematique summary
	 */
	generatePresenceSummary(
		thematiqueId: string,
		data?: any,
		asAdmin = true
	): Promise<AttestationPresenceThematiqueSummary> {
		const options = {
			withCredentials: true,
		};
		return this.http
			.post<AttestationPresenceThematiqueSummaryInterface>(
				`${this.uri(
					asAdmin
				)}/${thematiqueId}/presence-summary/generate`,
				data || {},
				options
			)
			.toPromise()
			.then((result) => new AttestationPresenceThematiqueSummary(result));
	}
}
